import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Baby, Search, Eye, MessageCircle, TrendingUp, Sparkles, ArrowLeft, Clock } from 'lucide-react';
import { Radar, RadarChart, PolarGrid, PolarAngleAxis, ResponsiveContainer } from 'recharts';
import { Input } from '../../../components/ui/input';
import { infantilAPI } from '../services/api';

const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0, transition: { duration: 0.35 } } };
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.05 } } };
const FIELD_COLORS = { 'bncc-ei-01': '#8E52AB', 'bncc-ei-02': '#F59E0B', 'bncc-ei-03': '#EC4899', 'bncc-ei-04': '#3B82F6', 'bncc-ei-05': '#10B981' };

export default function Student360Page() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => { infantilAPI.classes().then(setClasses); }, []);
  useEffect(() => { if (selectedClass) infantilAPI.classStudents(selectedClass).then(setStudents); }, [selectedClass]);
  useEffect(() => {
    if (studentId) {
      setLoading(true);
      infantilAPI.student360(studentId).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    } else { setLoading(false); }
  }, [studentId]);

  // Student picker
  if (!studentId) {
    const filtered = students.filter(s => (s.name || '').toLowerCase().includes(search.toLowerCase()));
    return (
      <motion.div className="max-w-2xl mx-auto space-y-6 py-8" initial="hidden" animate="visible" variants={stagger}>
        <motion.div variants={fadeUp} className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#DDF7EC] flex items-center justify-center mx-auto mb-4"><Baby className="h-8 w-8 text-[#2BB673]" /></div>
          <h1 className="text-xl font-bold text-slate-800">Criança 360</h1>
          <p className="text-sm text-slate-500 mt-1">Selecione uma turma e uma criança</p>
        </motion.div>
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm space-y-4">
          <select value={selectedClass} onChange={e => setSelectedClass(e.target.value)}
            className="w-full h-10 rounded-xl border border-slate-200 px-3 text-sm">
            <option value="">Selecione a turma...</option>
            {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          {selectedClass && <Input placeholder="Buscar criança..." value={search} onChange={e => setSearch(e.target.value)} className="rounded-xl" />}
          <div className="space-y-1 max-h-72 overflow-y-auto">
            {filtered.map(s => (
              <button key={s.id} onClick={() => navigate(`/v2/infantil/crianca/${s.id}`)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl hover:bg-slate-50 transition-all text-left">
                <div className="w-8 h-8 rounded-full bg-[#5B3B8C15] flex items-center justify-center text-[#64327D] text-sm font-bold">{(s.name||'?')[0]}</div>
                <span className="text-sm font-medium text-slate-700">{s.name}</span>
              </button>
            ))}
          </div>
        </motion.div>
      </motion.div>
    );
  }

  if (loading) return <div className="animate-pulse space-y-4"><div className="h-32 rounded-2xl bg-slate-100" /><div className="grid grid-cols-2 gap-4"><div className="h-64 rounded-2xl bg-slate-100" /><div className="h-64 rounded-2xl bg-slate-100" /></div></div>;

  const student = data?.student || {};
  const bncc = data?.bncc_radar || [];
  const dims = data?.dimensions_radar || [];
  const quotes = data?.highlighted_quotes || [];
  const recent = data?.recent_observations || [];
  const ctxDist = data?.context_distribution || {};
  const totalObs = data?.total_observations || 0;
  const maxBncc = Math.max(1, ...bncc.map(b => b.count));
  const maxDim = Math.max(1, ...dims.map(d => d.count));
  const bnccRadar = bncc.map(b => ({ subject: b.code, value: b.count, fullMark: maxBncc + 2 }));
  const dimRadar = dims.map(d => ({ subject: d.key?.substring(0, 8), value: d.count, fullMark: maxDim + 2 }));

  return (
    <motion.div className="space-y-6" initial="hidden" animate="visible" variants={stagger} data-testid="student-360-page">
      {/* Header */}
      <motion.div variants={fadeUp} className="rounded-2xl p-6 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #2E1A47, #64327D)' }}>
        <button onClick={() => navigate('/v2/infantil/crianca')} className="absolute top-4 left-4 p-2 rounded-xl bg-white/10 hover:bg-white/20 transition-colors"><ArrowLeft className="h-4 w-4 text-white" /></button>
        <div className="ml-12">
          <p className="text-xs text-white/40 uppercase tracking-widest mb-1">Criança 360</p>
          <h1 className="text-2xl font-bold text-white">{student.name || 'Aluno'}</h1>
          <div className="flex gap-4 mt-2 text-sm text-white/60">
            <span>{totalObs} observações</span>
            <span>{quotes.length} falas registradas</span>
            <span>{Object.keys(ctxDist).length} contextos</span>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-48 h-48 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #2BB673, transparent 70%)' }} />
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* BNCC Radar */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[#64327D]" /> Radar BNCC</h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={bnccRadar}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: '#64748b' }} />
              <Radar dataKey="value" stroke="#64327D" fill="#64327D" fillOpacity={0.25} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-5 gap-1 mt-2">
            {bncc.map((b, i) => (
              <div key={i} className="text-center">
                <div className="text-lg font-bold" style={{ color: b.color }}>{b.count}</div>
                <div className="text-[9px] text-slate-400 leading-tight">{b.field?.substring(0, 18)}</div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Dimensions Radar */}
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-2 flex items-center gap-2"><Sparkles className="h-4 w-4 text-[#2BB673]" /> Radar Dimensões</h3>
          <ResponsiveContainer width="100%" height={260}>
            <RadarChart data={dimRadar}>
              <PolarGrid stroke="#e2e8f0" />
              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 9, fill: '#64748b' }} />
              <Radar dataKey="value" stroke="#2BB673" fill="#2BB673" fillOpacity={0.25} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
          <div className="flex flex-wrap gap-1.5 mt-2">
            {dims.filter(d => d.count > 0).map((d, i) => (
              <span key={i} className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: d.color + '15', color: d.color }}>{d.dimension}: {d.count}</span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Quotes */}
      {quotes.length > 0 && (
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><MessageCircle className="h-4 w-4 text-[#64327D]" /> Falas Marcantes</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {quotes.map((q, i) => (
              <div key={i} className="p-3 rounded-xl bg-[#5B3B8C08] border border-[#5B3B8C10]">
                <p className="text-sm italic text-[#64327D]">"{q}"</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Context Distribution */}
      {Object.keys(ctxDist).length > 0 && (
        <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
          <h3 className="text-sm font-bold text-slate-800 mb-3">Distribuição por Contexto</h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(ctxDist).sort((a, b) => b[1] - a[1]).map(([ctx, cnt], i) => (
              <div key={i} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-50">
                <span className="text-sm text-slate-600">{ctx}</span>
                <span className="text-xs font-bold text-[#2BB673] bg-[#DDF7EC] px-2 py-0.5 rounded-full">{cnt}</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Timeline */}
      <motion.div variants={fadeUp} className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <h3 className="text-sm font-bold text-slate-800 mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-slate-500" /> Timeline Pedagógica</h3>
        {recent.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-8">Nenhuma observação registrada para esta criança.</p>
        ) : (
          <div className="space-y-3">
            {recent.map((obs, i) => (
              <div key={i} className="flex items-start gap-3 p-3 rounded-xl bg-slate-50">
                <div className="w-1.5 h-12 rounded-full flex-shrink-0" style={{ backgroundColor: FIELD_COLORS[obs.bncc_field_id] || '#94A3B8' }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700">{obs.title}</p>
                  <p className="text-xs text-slate-500 line-clamp-1">{obs.observation_text}</p>
                  <div className="flex gap-1.5 mt-1">
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DDF7EC] text-[#2BB673]">{obs.context}</span>
                    <span className="text-[10px] text-slate-400">{obs.date}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </motion.div>
    </motion.div>
  );
}
